/**
 * alertBus — module-scoped bridge between non-React code and the AlertProvider.
 *
 * Only the AlertProvider should call `setAlertApi(...)`. Anywhere else
 * (services, axios interceptors, callbacks, the toast shim) can call
 * `alertBus.success/error/warning/info` and the active provider will
 * render the alert.
 *
 * If no provider is mounted yet (e.g. a fetch error before App boots),
 * we fall back to console.warn so logs still capture the message.
 */

export type AlertType = "success" | "error" | "warning" | "info";

export interface AlertApi {
  show: (opts: { type?: AlertType; title?: string; message?: string; duration?: number }) => void;
  hide: () => void;
  success: (title?: string, message?: string, duration?: number) => void;
  error: (title?: string, message?: string, duration?: number) => void;
  warning: (title?: string, message?: string, duration?: number) => void;
  info: (title?: string, message?: string, duration?: number) => void;
}

let _api: AlertApi | null = null;

export function setAlertApi(api: AlertApi | null): void {
  _api = api;
}

function fallback(type: AlertType, title?: string, message?: string) {
  // eslint-disable-next-line no-console
  console.warn(`[alertBus:${type}] (no provider yet)`, title || "", message || "");
}

export const alertBus: AlertApi = {
  show: (opts) => {
    if (_api) _api.show(opts);
    else fallback(opts.type || "info", opts.title, opts.message);
  },
  hide: () => _api?.hide(),
  success: (title, message, duration) => {
    if (_api) _api.success(title, message, duration);
    else fallback("success", title, message);
  },
  error: (title, message, duration) => {
    if (_api) _api.error(title, message, duration);
    else fallback("error", title, message);
  },
  warning: (title, message, duration) => {
    if (_api) _api.warning(title, message, duration);
    else fallback("warning", title, message);
  },
  info: (title, message, duration) => {
    if (_api) _api.info(title, message, duration);
    else fallback("info", title, message);
  },
};
